/**
 * TutorialVideo
 * ─────────────
 * Three co-ordinated UI pieces that share one modal:
 *   • <TutorialVideoCard />  — large play-button card for the Hero section
 *   • <TutorialPill />       — floating bottom-right pill (dismissible, remembers)
 *   • <TutorialVideoModal /> — lightbox player, mounted once at the LandingPage root
 *
 * The three communicate via a single `useState` lifted in LandingPage.js.
 */

import React, { useEffect, useRef, useState } from "react";
import { Play, X } from "@phosphor-icons/react";
import { useLang } from "@/context/LangContext";

const VIDEO_SRC_EN = "/videos/tutorial.mp4";
const VIDEO_SRC_AR = "/videos/tutorial_ar.mp4";
const DISMISS_KEY = "tutorial_pill_dismissed_v1";

// ─── Hero card ────────────────────────────────────────────────
export const TutorialVideoCard = ({ onOpen }) => {
  const { t } = useLang();
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="tutorial-hero-card"
      className="group relative w-full overflow-hidden rounded-2xl border border-[#EAB308]/40 bg-gradient-to-br from-[#0F1418] via-[#111216] to-[#0B0F1A] shadow-[0_8px_28px_rgba(234,179,8,0.15)] px-5 py-4 sm:py-5 flex items-center gap-4 text-left transition-all hover:border-[#EAB308]/70 hover:shadow-[0_10px_32px_rgba(234,179,8,0.25)]"
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#EAB308]/10 blur-3xl pointer-events-none" />
      <div className="relative flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#EAB308] to-[#F97316] flex items-center justify-center shadow-[0_6px_20px_rgba(234,179,8,0.45)] transition-transform group-hover:scale-105">
        <span className="absolute inset-0 rounded-full bg-[#EAB308]/50 animate-ping" />
        <Play size={24} weight="fill" className="text-[#0B0F1A] translate-x-[1px]" />
      </div>
      <div className="relative flex-1 min-w-0">
        <div className="font-['Outfit'] text-[10px] font-bold tracking-[2.5px] uppercase text-[#EAB308] mb-1">
          {t('tut_eyebrow')}
        </div>
        <div className="font-['Outfit'] text-white font-bold text-base sm:text-lg leading-tight">
          {t('tut_card_title')}
        </div>
        <div className="text-slate-400 text-xs sm:text-sm mt-1 line-clamp-1">
          {t('tut_card_sub')}
        </div>
      </div>
    </button>
  );
};

// ─── Floating pill (bottom-right, dismissible) ────────────────
export const TutorialPill = ({ onOpen }) => {
  const { t, isRtl } = useLang();
  const [hidden, setHidden] = useState(true);
  useEffect(() => {
    try {
      const dismissed = typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1";
      setHidden(dismissed);
    } catch (_) { setHidden(false); }
  }, []);
  const dismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (_) {}
    setHidden(true);
  };
  if (hidden) return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="tutorial-floating-pill"
      className={`fixed z-40 bottom-24 sm:bottom-28 ${isRtl ? "left-5" : "right-5"} flex items-center gap-2 pl-3 pr-2 py-2 rounded-full bg-[#0B0F1A] border border-[#EAB308]/50 shadow-[0_8px_24px_rgba(0,0,0,0.5)] hover:border-[#EAB308] transition-all animate-fade-in-up`}
    >
      <span className="relative flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#EAB308] to-[#F97316] flex items-center justify-center">
        <span className="absolute inset-0 rounded-full bg-[#EAB308]/50 animate-ping" />
        <Play size={14} weight="fill" className="text-[#0B0F1A] translate-x-[0.5px]" />
      </span>
      <span className="font-['Outfit'] text-sm font-semibold text-white whitespace-nowrap">
        {t('tut_pill_label')}
      </span>
      <span
        onClick={dismiss}
        role="button"
        aria-label="Dismiss"
        data-testid="tutorial-pill-dismiss"
        className="ml-1 w-7 h-7 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-200 hover:bg-white/5 transition-colors cursor-pointer"
      >
        <X size={14} weight="bold" />
      </span>
    </button>
  );
};

// ─── Lightbox modal ───────────────────────────────────────────
export const TutorialVideoModal = ({ open, onClose }) => {
  const { t, lang } = useLang();
  const videoRef = useRef(null);
  const videoSrc = lang === "ar" ? VIDEO_SRC_AR : VIDEO_SRC_EN;
  useEffect(() => {
    if (!open) return;
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    // Autoplay on open (muted first so browsers allow it, then unmute)
    const v = videoRef.current;
    if (v) {
      try { v.currentTime = 0; v.play(); } catch (_) {}
    }
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", esc);
      document.body.style.overflow = "";
      if (v) { try { v.pause(); } catch (_) {} }
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      data-testid="tutorial-video-modal"
    >
      <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          data-testid="tutorial-video-close"
          aria-label="Close video"
          className="absolute -top-10 right-0 text-white hover:text-[#EAB308] transition-colors flex items-center gap-2 text-sm font-['Outfit']"
        >
          {t('tut_close')} <X size={20} weight="bold" />
        </button>
        <div className="rounded-xl overflow-hidden border border-[#EAB308]/30 shadow-[0_20px_80px_rgba(234,179,8,0.25)] bg-black">
          <video
            key={videoSrc}
            ref={videoRef}
            src={videoSrc}
            controls
            playsInline
            preload="metadata"
            className="w-full h-auto block"
            data-testid="tutorial-video-player"
          />
        </div>
      </div>
    </div>
  );
};
